import { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import {
  Funnel, Users, Buildings, CurrencyInr, ChartLine,
  ChatCircle, Lightning, ShieldCheck, ArrowRight,
  Moon, Sun, List, X
} from '@phosphor-icons/react';
import { useTheme } from '@/contexts/ThemeContext';

gsap.registerPlugin(ScrollTrigger);

const FEATURES = [
  { icon: Users, title: 'Lead Management', desc: 'Capture, score, and nurture leads from 10+ sources. Parent-student dual-contact workflows built for Indian education.', color: '#2B6EF0', slug: 'lead-management' },
  { icon: Funnel, title: 'Visual Pipeline', desc: 'Drag-and-drop Kanban board. Move leads through inquiry, demo, application, enrollment — all in one view.', color: '#8B5CF6', slug: 'admissions-pipeline' },
  { icon: Buildings, title: 'Multi-Branch Ops', desc: 'Manage 1 centre or 100 franchises. Role-based access, branch-level analytics, and centralised control.', color: '#10A882', slug: 'multi-branch' },
  { icon: CurrencyInr, title: 'Fee Collection', desc: 'Invoicing, partial payments, UPI/cash/bank tracking. Real-time outstanding dashboards per student.', color: '#E5A100', slug: 'fee-collection' },
  { icon: ChartLine, title: 'Analytics & Reports', desc: 'Source ROI, counselor leaderboards, conversion funnels, revenue tracking. Data-driven decisions.', color: '#E04B2A', slug: 'analytics' },
  { icon: ChatCircle, title: 'Communication Hub', desc: 'WhatsApp, SMS, email — unified messaging with templates. Never miss a follow-up again.', color: '#06B6D4', slug: 'communication' },
  { icon: Lightning, title: 'Automation Engine', desc: 'Trigger-based workflows. Auto-assign leads, send reminders, update stages — hands-free.', color: '#F97316', slug: 'automation' },
  { icon: ShieldCheck, title: 'Audit & Compliance', desc: 'Full activity logging, RBAC, consent tracking. Know who did what, when, and why.', color: '#EC4899', slug: null },
];

const SCREENSHOTS = [
  { src: '/screenshots/dashboard.png', label: 'Dashboard', desc: 'Real-time KPIs, source analytics, counselor performance — everything at a glance.' },
  { src: '/screenshots/pipeline.png', label: 'Pipeline', desc: 'Visual Kanban board with drag-and-drop. See every lead in your funnel.' },
  { src: '/screenshots/leads.png', label: 'Leads', desc: 'Powerful table view with search, filters, and instant access to lead profiles.' },
  { src: '/screenshots/fees.png', label: 'Fees', desc: 'Invoice management, payment tracking, outstanding dues — all in one place.' },
  { src: '/screenshots/communication.png', label: 'Messaging', desc: 'WhatsApp, SMS, and email — unified inbox with templates and bulk send.' },
  { src: '/screenshots/automations.png', label: 'Automation', desc: 'Trigger-based workflows that run 24/7. Auto-assign, remind, and notify.' },
  { src: '/screenshots/reports.png', label: 'Reports', desc: 'Source ROI, finance breakdown, counselor leaderboard — deep analytics.' },
];

const STATS = [
  { value: 10, suffix: '+', label: 'Lead Sources' },
  { value: 7, suffix: '', label: 'Integrations' },
  { value: 52, suffix: '', label: 'Help Articles' },
  { value: 24, suffix: '/7', label: 'Automation' },
];



export default function LandingPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [activeShot, setActiveShot] = useState(0);
  const [mobileMenu, setMobileMenu] = useState(false);
  const containerRef = useRef(null);
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const screenshotRef = useRef(null);
  const statsRef = useRef(null);
  const testimonialsRef = useRef(null);
  const pricingRef = useRef(null);

  useGSAP(() => {
    const ctx = gsap.context(() => {
      // Hero entrance
      const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      heroTl
        .from('.hero-badge', { y: 20, opacity: 0, duration: 0.6 })
        .from('.hero-title-line', { y: 60, opacity: 0, stagger: 0.12, duration: 0.8 }, '-=0.3')
        .from('.hero-subtitle', { y: 20, opacity: 0, duration: 0.6 }, '-=0.4')
        .from('.hero-ctas', { y: 20, opacity: 0, duration: 0.5 }, '-=0.3')
        .from('.hero-image', { y: 40, opacity: 0, scale: 0.96, duration: 1, ease: 'power2.out' }, '-=0.4')
        .from('.hero-stat', { y: 20, opacity: 0, stagger: 0.08, duration: 0.5 }, '-=0.6');

      // Hero image parallax
      gsap.to('.hero-image', {
        yPercent: -6,
        ease: 'none',
        scrollTrigger: { trigger: heroRef.current, start: 'top top', end: 'bottom top', scrub: 1 },
      });

      // Features stagger
      gsap.from('.feature-card', {
        y: 40, opacity: 0, stagger: 0.08, duration: 0.6, immediateRender: false,
        scrollTrigger: { trigger: featuresRef.current, start: 'top 80%' },
      });

      // Screenshot section
      gsap.from('.screenshot-browser', {
        y: 60, opacity: 0, scale: 0.95, duration: 1, immediateRender: false,
        scrollTrigger: { trigger: screenshotRef.current, start: 'top 75%' },
      });

      // Stats counters
      if (statsRef.current) {
        const counters = statsRef.current.querySelectorAll('.stat-value');
        counters.forEach(el => {
          const target = parseFloat(el.dataset.value);
          gsap.fromTo(el, { textContent: 0 }, {
            textContent: target,
            duration: 2,
            ease: 'power2.out',
            snap: { textContent: target % 1 === 0 ? 1 : 0.1 },
            scrollTrigger: { trigger: el, start: 'top 85%' },
            onUpdate: function() {
              const val = parseFloat(gsap.getProperty(el, 'textContent'));
              el.textContent = target % 1 === 0 ? Math.round(val).toLocaleString('en-IN') : val.toFixed(1);
            }
          });
        });
      }

      // Integrations — spring-in from center
      gsap.from('.integration-node', {
        scale: 0, opacity: 0, stagger: 0.12, duration: 0.8, ease: 'back.out(1.7)', immediateRender: false,
        scrollTrigger: { trigger: testimonialsRef.current, start: 'top 75%' },
      });

      // Pricing
      gsap.from('.pricing-card', {
        y: 40, opacity: 0, stagger: 0.1, duration: 0.6, immediateRender: false,
        scrollTrigger: { trigger: pricingRef.current, start: 'top 80%' },
      });
    }, containerRef);

    return () => ctx.revert();
  }, { scope: containerRef });

  return (
    <div ref={containerRef} className="bg-background text-foreground overflow-x-hidden" data-testid="landing-page">
      {/* ═══ NAVBAR ═══ */}
      <header>
      <nav role="navigation" aria-label="Main navigation" className="fixed top-0 left-0 right-0 z-50 h-16 flex items-center justify-between px-6 lg:px-12 border-b border-transparent" style={{ backdropFilter: 'blur(24px) saturate(1.3)', WebkitBackdropFilter: 'blur(24px) saturate(1.3)', backgroundColor: theme === 'dark' ? 'rgba(15,15,15,0.85)' : 'rgba(247,246,243,0.85)' }} data-testid="landing-nav">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#E04B2A' }}>
            <span className="text-xs font-extrabold text-white font-display">A</span>
          </div>
          <span className="font-display font-bold text-lg tracking-tight text-foreground">Admitrix</span>
        </div>
        <div className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors">Features</a>
          <a href="#screenshots" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors">Product</a>
          <Link to="/pricing" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors">Pricing</Link>
          <Link to="/docs" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors">Docs</Link>
          <a href="#integrations" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors">Integrations</a>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={toggleTheme} className="p-2 hover:bg-secondary rounded-lg transition-colors text-muted-foreground" data-testid="landing-theme-toggle">
            {theme === 'dark' ? <Sun size={16} weight="bold" /> : <Moon size={16} weight="bold" />}
          </button>
          <button onClick={() => navigate('/login')} className="text-[13px] text-muted-foreground hover:text-foreground transition-colors hidden sm:block" data-testid="nav-signin">Sign in</button>
          <button onClick={() => navigate('/contact')} className="hidden sm:flex h-9 px-5 rounded-lg text-[13px] font-bold text-white items-center gap-1.5 transition-transform hover:scale-[1.02]" style={{ backgroundColor: '#E04B2A' }} data-testid="nav-get-started">Get Started <ArrowRight size={14} weight="bold" /></button>
          <button onClick={() => setMobileMenu(!mobileMenu)} className="md:hidden p-2 text-foreground">
            {mobileMenu ? <X size={20} /> : <List size={20} />}
          </button>
        </div>
      </nav>
      {mobileMenu && (
        <div className="fixed inset-0 z-40 bg-background pt-20 px-6 flex flex-col gap-6 md:hidden">
          <a href="#features" onClick={() => setMobileMenu(false)} className="text-lg font-display font-bold">Features</a>
          <a href="#screenshots" onClick={() => setMobileMenu(false)} className="text-lg font-display font-bold">Product</a>
          <Link to="/contact" onClick={() => setMobileMenu(false)} className="text-lg font-display font-bold">Contact</Link>
          <button onClick={() => { navigate('/contact'); setMobileMenu(false); }} className="h-12 rounded-lg text-sm font-bold text-white mt-4" style={{ backgroundColor: '#E04B2A' }}>Get Started</button>
        </div>
      )}
      </header>

      <main>
      {/* ═══ HERO ═══ */}
      <section ref={heroRef} className="pt-32 pb-12 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="hero-section">
        <div className="max-w-3xl">
          <div className="hero-badge inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Built for Indian Education
          </div>
          <h1 className="font-display font-extrabold tracking-[-0.03em] leading-[1.05]">
            <span className="hero-title-line block text-[clamp(2.5rem,5.5vw,5rem)]">The Admissions</span>
            <span className="hero-title-line block text-[clamp(2.5rem,5.5vw,5rem)]" style={{ color: '#E04B2A' }}>Operating System.</span>
          </h1>
          <p className="hero-subtitle text-lg text-muted-foreground mt-6 max-w-xl leading-relaxed">
            Manage leads, admissions, fees, and multi-branch operations from one platform.
            Built for coaching classes, schools, and education franchises across India.
          </p>
          <div className="hero-ctas flex items-center gap-3 mt-8">
            <button onClick={() => navigate('/contact')} className="h-12 px-7 rounded-lg text-sm font-bold text-white flex items-center gap-2 transition-transform hover:scale-[1.02] active:scale-[0.98]" style={{ backgroundColor: '#E04B2A' }} data-testid="hero-cta-primary">
              Request a Demo <ArrowRight size={16} weight="bold" />
            </button>
            <a href="#screenshots" className="h-12 px-7 rounded-lg text-sm font-semibold border border-border text-foreground flex items-center gap-2 hover:bg-secondary transition-colors" data-testid="hero-cta-secondary">
              See it in action
            </a>
          </div>
          <div className="flex gap-8 mt-10">
            {[['10+', 'Lead Sources'], ['7', 'Integrations'], ['24/7', 'Automation']].map(([v, l]) => (
              <div key={l} className="hero-stat">
                <p className="font-display text-2xl font-extrabold text-foreground">{v}</p>
                <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider mt-0.5">{l}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="hero-image mt-14 relative">
          <div className="rounded-xl overflow-hidden border border-border shadow-2xl" style={{ boxShadow: theme === 'dark' ? '0 40px 80px -20px rgba(0,0,0,0.5)' : '0 40px 80px -20px rgba(0,0,0,0.15)' }}>
            <div className="h-8 bg-card border-b border-border flex items-center px-4 gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400" /><span className="w-2.5 h-2.5 rounded-full bg-yellow-400" /><span className="w-2.5 h-2.5 rounded-full bg-green-400" />
              <span className="text-[10px] font-mono text-muted-foreground ml-3">app.admitrix.com/dashboard</span>
            </div>
            <img src="/screenshots/dashboard.png" alt="Admitrix Dashboard" className="w-full" loading="eager" />
          </div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section id="features" ref={featuresRef} className="py-24 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="features-section">
        <div className="max-w-xl mb-14">
          <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground mb-3" style={{ color: '#E04B2A' }}>FEATURES</p>
          <h2 className="font-display text-4xl font-extrabold text-foreground tracking-tight leading-[1.1]">Everything you need to<br />run admissions at scale.</h2>
          <p className="text-base text-muted-foreground mt-4 leading-relaxed">From a single coaching centre to a national franchise — Admitrix grows with you.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            const Wrapper = f.slug ? Link : 'div';
            const wrapperProps = f.slug ? { to: `/features/${f.slug}` } : {};
            return (
              <Wrapper key={f.title} {...wrapperProps} className="feature-card p-5 rounded-xl border border-border bg-card hover:border-foreground/10 transition-all duration-300 group block" data-testid={`feature-${f.title.toLowerCase().replace(/ /g, '-')}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ backgroundColor: `${f.color}12`, color: f.color }}>
                  <Icon size={18} weight="bold" />
                </div>
                <h3 className="font-display text-sm font-bold text-foreground mb-1.5">{f.title}</h3>
                <p className="text-[12px] text-muted-foreground leading-relaxed">{f.desc}</p>
                {f.slug && (
                  <span className="inline-flex items-center gap-1 mt-3 text-[11px] font-semibold transition-colors" style={{ color: f.color }}>
                    Learn more <ArrowRight size={12} weight="bold" />
                  </span>
                )}
              </Wrapper>
            );
          })}
        </div>
      </section>

      {/* ═══ SCREENSHOTS ═══ */}
      <ProductShowcase theme={theme} screenshotRef={screenshotRef} activeShot={activeShot} setActiveShot={setActiveShot} />

      {/* ═══ STATS ═══ */}
      <section ref={statsRef} className="py-20 border-y border-border" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-display text-5xl font-extrabold text-foreground">
                <span className="stat-value" data-value={s.value}>0</span><span className="text-3xl" style={{ color: '#E04B2A' }}>{s.suffix}</span>
              </p>
              <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider mt-2">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ INTEGRATIONS ═══ */}
      <section id="integrations" ref={testimonialsRef} className="py-28 px-6 lg:px-12 relative overflow-hidden" data-testid="integrations-section">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <p className="text-[10px] font-mono uppercase tracking-[0.15em] mb-3" style={{ color: '#E04B2A' }}>INTEGRATIONS</p>
            <h2 className="font-display text-4xl font-extrabold text-foreground tracking-tight leading-[1.1]">Connected to your<br />entire stack.</h2>
            <p className="text-base text-muted-foreground mt-4 max-w-lg mx-auto">Payment gateways, ad platforms, messaging services, and calendars — built in, not bolted on.</p>
          </div>

          {/* Desktop: Orbital hub */}
          <div className="hidden md:flex items-center justify-center relative" style={{ height: '380px' }}>
            {/* Orbit rings */}
            <div className="absolute w-[280px] h-[280px] rounded-full border border-border/30" style={{ animation: 'spin-slow 60s linear infinite' }} />
            <div className="absolute w-[480px] h-[480px] rounded-full border border-border/15" />

            {/* Pulse ring */}
            <div className="absolute w-[280px] h-[280px] rounded-full" style={{ animation: 'pulse-ring 3s ease-in-out infinite', border: '1px solid rgba(224,75,42,0.15)' }} />

            {/* Center hub */}
            <div className="integration-node relative z-20">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E04B2A, #C93E22)', boxShadow: '0 0 60px rgba(224,75,42,0.25), 0 0 120px rgba(224,75,42,0.1)' }}>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none"><rect x="4" y="4" width="24" height="24" rx="4" fill="white" fillOpacity="0.2"/><text x="16" y="22" textAnchor="middle" fill="white" fontSize="16" fontWeight="800" fontFamily="system-ui">A</text></svg>
              </div>
            </div>

            {/* Satellite nodes — positioned with transform from center */}
            {[
              { name: 'Razorpay', color: '#2B84EA', desc: 'Payments', tx: -200, ty: -100, icon: <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6"><path d="M22 10l-2 8H8L4 2H1" stroke="#2B84EA" strokeWidth="2" strokeLinecap="round"/><path d="M7.5 14l3-8h5l-1.5 4H19" stroke="#2B84EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg> },
              { name: 'WhatsApp', color: '#25D366', desc: 'Messaging', tx: 0, ty: -160, icon: <svg viewBox="0 0 24 24" fill="#25D366" className="w-6 h-6"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> },
              { name: 'Meta', color: '#0082FB', desc: 'Lead Ads', tx: 200, ty: -100, icon: <svg viewBox="0 0 24 24" fill="#0082FB" className="w-6 h-6"><path d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.52 1.49-3.93 3.78-3.93 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 008.44-9.9c0-5.53-4.5-10.02-10-10.02z"/></svg> },
              { name: 'Google', color: '#4285F4', desc: 'Calendar', tx: -130, ty: 130, icon: <svg viewBox="0 0 24 24" className="w-6 h-6"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg> },
              { name: 'SendGrid', color: '#1A82E2', desc: 'Email', tx: 130, ty: 130, icon: <svg viewBox="0 0 24 24" fill="#1A82E2" className="w-6 h-6"><path d="M2 2h6.67v6.67H2V2zm6.67 0H15.33v6.67H8.67V2zM15.33 2H22v6.67h-6.67V2zM2 8.67h6.67v6.67H2V8.67zm6.67 0h6.67v6.67H8.67V8.67zM2 15.33h6.67V22H2v-6.67zm6.67 0h6.67V22H8.67v-6.67z"/></svg> },
              { name: 'MSG91', color: '#FF6B35', desc: 'SMS', tx: 0, ty: 160, icon: <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z" stroke="#FF6B35" strokeWidth="2"/><path d="M22 6l-10 7L2 6" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><text x="12" y="19" textAnchor="middle" fill="#FF6B35" fontSize="5" fontWeight="800">91</text></svg> },
            ].map((int, i) => (
              <div key={int.name} className="absolute z-10 integration-node group" style={{ transform: `translate(${int.tx}px, ${int.ty}px)` }} data-testid={`integration-${int.name.toLowerCase()}`}>
                {/* Connection line to center */}
                <svg className="absolute pointer-events-none" style={{ left: '50%', top: '50%', width: '1px', height: '1px', overflow: 'visible' }}>
                  <line x1="0" y1="0" x2={-int.tx} y2={-int.ty} stroke={theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'} strokeWidth="1" strokeDasharray="3 6">
                    <animate attributeName="stroke-dashoffset" from="9" to="0" dur={`${1.5 + i * 0.2}s`} repeatCount="indefinite" />
                  </line>
                </svg>
                <div className="flex flex-col items-center gap-2.5 relative">
                  {/* Glow */}
                  <div className="absolute -inset-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" style={{ backgroundColor: `${int.color}30` }} />
                  {/* Node */}
                  <div className="relative w-16 h-16 rounded-full border bg-card flex items-center justify-center transition-all duration-300 group-hover:scale-110" style={{ borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', boxShadow: `0 4px 20px ${int.color}10` }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${int.color}12` }}>
                      {int.icon}
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-[11px] font-bold text-foreground whitespace-nowrap">{int.name}</p>
                    <p className="text-[8px] text-muted-foreground font-mono uppercase tracking-[0.15em]">{int.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile: card grid */}
          <div className="md:hidden grid grid-cols-2 gap-3">
            {[
              { name: 'Razorpay', color: '#2B84EA', desc: 'Payments' },
              { name: 'WhatsApp', color: '#25D366', desc: 'Messaging' },
              { name: 'Meta', color: '#0082FB', desc: 'Lead Ads' },
              { name: 'Google', color: '#4285F4', desc: 'Calendar' },
              { name: 'SendGrid', color: '#1A82E2', desc: 'Email' },
              { name: 'MSG91', color: '#FF6B35', desc: 'SMS' },
            ].map((int) => (
              <div key={int.name} className="integration-node p-4 rounded-xl border border-border bg-card flex items-center gap-3 group hover:border-foreground/15 transition-all">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110" style={{ backgroundColor: `${int.color}12` }}>
                  <span className="text-sm font-extrabold font-display" style={{ color: int.color }}>{int.name.charAt(0)}</span>
                </div>
                <div>
                  <p className="text-[12px] font-bold text-foreground">{int.name}</p>
                  <p className="text-[9px] text-muted-foreground font-mono uppercase tracking-[0.12em]">{int.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <style>{`
          @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          @keyframes pulse-ring { 0%, 100% { transform: scale(1); opacity: 0.3; } 50% { transform: scale(1.08); opacity: 0.1; } }
        `}</style>
      </section>

      {/* ═══ CTA SECTION (replaces pricing) ═══ */}
      <section id="pricing" ref={pricingRef} className="py-24 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="pricing-section">
        <div className="pricing-card max-w-3xl mx-auto text-center rounded-2xl p-12 border border-border relative overflow-hidden" style={{ background: theme === 'dark' ? 'linear-gradient(135deg, rgba(224,75,42,0.08), transparent)' : 'linear-gradient(135deg, rgba(224,75,42,0.05), transparent)' }}>
          <p className="text-[10px] font-mono uppercase tracking-[0.15em] mb-4" style={{ color: '#E04B2A' }}>PRICING</p>
          <h2 className="font-display text-4xl font-extrabold text-foreground tracking-tight leading-[1.1]">Ready to transform<br />your admissions?</h2>
          <p className="text-base text-muted-foreground mt-4 max-w-md mx-auto">Pricing tailored to your institute size and needs. Get in touch and we'll find the right plan for you.</p>
          <div className="flex justify-center gap-3 mt-8">
            <button onClick={() => navigate('/contact')} className="h-12 px-8 rounded-lg text-sm font-bold text-white flex items-center gap-2 transition-transform hover:scale-[1.02]" style={{ backgroundColor: '#E04B2A' }} data-testid="cta-contact-sales">
              Contact Sales <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-24 px-6 lg:px-12" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center rounded-2xl p-12 border border-border relative overflow-hidden" style={{ background: theme === 'dark' ? 'linear-gradient(135deg, rgba(224,75,42,0.08), transparent)' : 'linear-gradient(135deg, rgba(224,75,42,0.05), transparent)' }}>
          <h2 className="font-display text-4xl font-extrabold text-foreground tracking-tight leading-[1.1]">See Admitrix<br />in action.</h2>
          <p className="text-base text-muted-foreground mt-4 max-w-md mx-auto">Built for Indian education — coaching classes, schools, and franchises. Book a personalized demo with our team.</p>
          <div className="flex justify-center gap-3 mt-8">
            <button onClick={() => navigate('/contact')} className="h-12 px-8 rounded-lg text-sm font-bold text-white flex items-center gap-2 transition-transform hover:scale-[1.02]" style={{ backgroundColor: '#E04B2A' }} data-testid="cta-final">
              Request a Demo <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      </section>

      </main>
      {/* ═══ FOOTER ═══ */}
      <footer className="border-t border-border py-12 px-6 lg:px-12" data-testid="footer">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#E04B2A' }}>
                <span className="text-[10px] font-extrabold text-white font-display">A</span>
              </div>
              <span className="font-display font-bold text-sm text-foreground">Admitrix</span>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">The admissions operating system for Indian education.</p>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-3 font-bold">Product</p>
            <div className="space-y-2 text-[12px] text-muted-foreground">
              <Link to="/features/lead-management" className="block hover:text-foreground transition-colors">Lead Management</Link>
              <Link to="/features/admissions-pipeline" className="block hover:text-foreground transition-colors">Pipeline</Link>
              <Link to="/features/fee-collection" className="block hover:text-foreground transition-colors">Fee Collection</Link>
              <Link to="/contact" className="block hover:text-foreground transition-colors">Contact</Link>
            </div>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-3 font-bold">Platform</p>
            <div className="space-y-2 text-[12px] text-muted-foreground">
              <Link to="/features/multi-branch" className="block hover:text-foreground transition-colors">Multi-Branch</Link>
              <Link to="/features/communication" className="block hover:text-foreground transition-colors">Communication</Link>
              <Link to="/features/analytics" className="block hover:text-foreground transition-colors">Analytics</Link>
              <Link to="/features/automation" className="block hover:text-foreground transition-colors">Automation</Link>
            </div>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-3 font-bold">Legal</p>
            <div className="space-y-2 text-[12px] text-muted-foreground">
              <Link to="/privacy" className="block hover:text-foreground transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="block hover:text-foreground transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-6 border-t border-border flex items-center justify-between">
          <p className="text-[10px] text-muted-foreground font-mono">&copy; 2026 Admitrix. All rights reserved.</p>
          <p className="text-[10px] text-muted-foreground font-mono">Made in India</p>
        </div>
      </footer>
    </div>
  );
}

function ProductShowcase({ theme, screenshotRef, activeShot, setActiveShot }) {
  const prevShotRef = useRef(activeShot);
  const autoplayRef = useRef(null);
  const [loadedMap, setLoadedMap] = useState({});

  // Progressive image loading — first image eager, rest in background
  useEffect(() => {
    SCREENSHOTS.forEach((s, i) => {
      const img = new Image();
      img.onload = () => setLoadedMap(prev => ({ ...prev, [i]: true }));
      img.src = s.src;
    });
  }, []);

  const allLoaded = Object.keys(loadedMap).length === SCREENSHOTS.length;
  const activeLoaded = !!loadedMap[activeShot];

  // Start autoplay only after first image is loaded
  useEffect(() => {
    if (!loadedMap[0]) return;
    autoplayRef.current = setInterval(() => {
      setActiveShot(prev => (prev + 1) % SCREENSHOTS.length);
    }, 5000);
    return () => clearInterval(autoplayRef.current);
  }, [loadedMap, setActiveShot]);

  const handleTabClick = useCallback((i) => {
    clearInterval(autoplayRef.current);
    setActiveShot(i);
    autoplayRef.current = setInterval(() => {
      setActiveShot(prev => (prev + 1) % SCREENSHOTS.length);
    }, 5000);
  }, [setActiveShot]);

  useEffect(() => { prevShotRef.current = activeShot; }, [activeShot]);

  const shot = SCREENSHOTS[activeShot];
  const loadProgress = Math.round((Object.keys(loadedMap).length / SCREENSHOTS.length) * 100);

  return (
    <section id="screenshots" ref={screenshotRef} className="py-24 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="screenshots-section">
      <div className="text-center max-w-xl mx-auto mb-14">
        <p className="text-[10px] font-mono uppercase tracking-[0.15em] mb-3" style={{ color: '#E04B2A' }}>PRODUCT</p>
        <h2 className="font-display text-4xl font-extrabold text-foreground tracking-tight leading-[1.1]">See Admitrix in action</h2>
        <p className="text-base text-muted-foreground mt-4">Every screen designed for speed and clarity. Zero learning curve.</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-col items-center gap-4 mb-10">
        <div className="flex flex-wrap justify-center gap-1.5">
          {SCREENSHOTS.map((s, i) => (
            <button key={s.label} onClick={() => handleTabClick(i)} data-testid={`shot-tab-${s.label.toLowerCase()}`}
              className={`relative px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-300 overflow-hidden ${
                activeShot === i ? 'text-white' : 'text-muted-foreground hover:text-foreground border border-border hover:bg-secondary'
              }`}
              style={activeShot === i ? { backgroundColor: '#E04B2A' } : {}}
            >
              {s.label}
              {activeShot === i && (
                <span key={`prog-${activeShot}`} className="absolute bottom-0 left-0 h-[2px] bg-white/40 origin-left" style={{ animation: 'showcase-progress 5s linear forwards', width: '100%' }} />
              )}
            </button>
          ))}
        </div>
        <div className="flex gap-1.5">
          {SCREENSHOTS.map((_, i) => (
            <div key={i} className="h-1 rounded-full transition-all duration-500 ease-out" style={{
              width: activeShot === i ? '24px' : '6px',
              backgroundColor: activeShot === i ? '#E04B2A' : theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
            }} />
          ))}
        </div>
      </div>

      {/* Browser mockup */}
      <div className="screenshot-browser relative" style={{ perspective: '1200px' }}>
        <div className="rounded-xl overflow-hidden border border-border" style={{
          boxShadow: theme === 'dark'
            ? '0 40px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)'
            : '0 40px 80px -20px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
          transform: 'rotateX(2deg)', transformOrigin: 'center bottom',
        }}>
          {/* Browser chrome */}
          <div className="h-10 bg-card border-b border-border flex items-center px-4 gap-2">
            <div className="flex gap-1.5">
              <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
              <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
              <span className="w-3 h-3 rounded-full bg-[#28C840]" />
            </div>
            <div className="flex-1 mx-4">
              <div className="h-6 rounded-md bg-secondary/60 flex items-center px-3 max-w-md mx-auto">
                <svg className="w-3 h-3 text-muted-foreground/50 mr-2 flex-shrink-0" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5"/><path d="M11 11l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                <span className="text-[10px] font-mono text-muted-foreground/60">app.admitrix.in/{shot.label.toLowerCase()}</span>
              </div>
            </div>
            {/* Loading indicator in browser chrome */}
            {!allLoaded && (
              <div className="absolute top-10 left-0 right-0 h-[2px] bg-border overflow-hidden z-10">
                <div className="h-full transition-all duration-700 ease-out" style={{ width: `${loadProgress}%`, background: 'linear-gradient(90deg, #E04B2A, #F97316)' }} />
              </div>
            )}
          </div>

          {/* Screenshot area */}
          <div className="relative overflow-hidden bg-card" style={{ aspectRatio: '1440/900' }}>
            {/* Shimmer skeleton — visible until active image loads */}
            {!activeLoaded && (
              <div className="absolute inset-0 z-10">
                <div className="absolute inset-0 bg-card" />
                <div className="absolute inset-0 showcase-shimmer" style={{
                  background: `linear-gradient(90deg, transparent 0%, ${theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'} 50%, transparent 100%)`,
                  backgroundSize: '200% 100%',
                }} />
                {/* Skeleton layout mimicking a dashboard */}
                <div className="absolute inset-0 p-6 flex gap-4">
                  <div className="w-48 flex-shrink-0 space-y-3">
                    <div className="h-8 rounded bg-secondary/40 w-28" />
                    <div className="space-y-2 mt-6">
                      {[...Array(8)].map((_, i) => <div key={i} className="h-4 rounded bg-secondary/30" style={{ width: `${60 + Math.random() * 40}%` }} />)}
                    </div>
                  </div>
                  <div className="flex-1 space-y-4">
                    <div className="h-6 rounded bg-secondary/40 w-32" />
                    <div className="grid grid-cols-4 gap-3">
                      {[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded-lg bg-secondary/30" />)}
                    </div>
                    <div className="grid grid-cols-2 gap-3 flex-1">
                      <div className="h-40 rounded-lg bg-secondary/20" />
                      <div className="h-40 rounded-lg bg-secondary/20" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Stacked images — slow dissolve crossfade */}
            {SCREENSHOTS.map((s, i) => (
              <img
                key={s.label}
                src={s.src}
                alt={`Admitrix ${s.label} — ${s.desc}`}
                loading={i === 0 ? 'eager' : 'lazy'}
                style={{
                  position: i === 0 ? 'relative' : 'absolute',
                  top: 0, left: 0, width: '100%',
                  opacity: activeShot === i && loadedMap[i] ? 1 : 0,
                  transition: 'opacity 1s cubic-bezier(0.4, 0, 0.2, 1)',
                  zIndex: activeShot === i ? 2 : 1,
                }}
              />
            ))}
          </div>
        </div>

        {/* Reflection */}
        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-3/4 h-16 rounded-full blur-2xl" style={{ backgroundColor: theme === 'dark' ? 'rgba(224,75,42,0.06)' : 'rgba(224,75,42,0.04)' }} />
      </div>

      {/* Caption */}
      <div className="mt-8 text-center" key={activeShot}>
        <h3 className="font-display text-xl font-bold text-foreground showcase-caption">{shot.label}</h3>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto showcase-caption" style={{ animationDelay: '0.05s' }}>{shot.desc}</p>
      </div>

      <style>{`
        @keyframes showcase-progress { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        .showcase-caption { animation: caption-in 0.4s ease-out both; }
        @keyframes caption-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .screenshot-browser { transition: transform 0.4s cubic-bezier(0.16,1,0.3,1); }
        .screenshot-browser:hover { transform: translateY(-4px); }
        @keyframes shimmer-slide { from { background-position: 200% 0; } to { background-position: -200% 0; } }
        .showcase-shimmer { animation: shimmer-slide 1.8s ease-in-out infinite; }
      `}</style>
    </section>
  );
}
